import React, { ReactElement, useCallback, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { QueryResult, useMutation, useQuery } from '@apollo/client';
import { Query } from '@apollo/client/react/components';
import { ModalContainer, ModalContent, ModalFooter, ModalHeader } from 'components/@common/Modal';
import { Formik } from 'formik';
import { Button } from '@material-ui/core';
import * as Yup from 'yup';
import SEND_LICENSE_ASSIGNMENT_EMAIL from 'graph/email/send-license-assignment-mutation';
import SEND_USER_INVITE_EMAIL from 'graph/email/send-user-invite-email-mutation';
import GET_ORGANIZATIONS_AND_ROLES_QUERY from 'graph/organization/get-organizations-and-roles-query';
import CREATE_USER from 'graph/user/create-user-mutation';
import GET_MY_ORGANIZATIONS_QUERY from 'graph/user/get-my-organizations-query';
import UPDATE_USER_LICENSES from 'graph/user/update-user-licenses-mutation';
import useStore from 'store';
import useUiStore from 'store/ui';
import useUserStore from 'store/user';
import { ProductWithCurriculum } from 'types';
import { AlertVariant } from 'types/enums';
import {
  Organization,
  OrganizationLicenseUsage,
  OrganizationRole
} from 'types/schema-generated-types';
import buildEmailUsers from 'utilities/build-email-users';
import Form from './components/Form';
import { buildCreateUserPayload } from './utils';
import { buildUpdateUserLicensePayload } from '../../utils/update-user-mutation-utils';
import { getAdminUserManagementUsers } from '../../utils/update-cache';

const InviteUserModalSchema = Yup.object().shape({
  firstName: Yup.string().required('Required'),
  lastName: Yup.string().required('Required'),
  email: Yup.string()
    .email('Invalid email')
    .required('Required'),
  organizationRoles: Yup.array()
    .min(1)
    .required()
});

const InviteUserModal = (): ReactElement => {
  const { t } = useTranslation('InviteUserModal');
  const [, { openAlert }] = useStore();
  const [, { closeModal }] = useUiStore();
  const [{ me }] = useUserStore();
  const { data: myOrgsResponse } = useQuery(GET_MY_ORGANIZATIONS_QUERY, {
    fetchPolicy: 'no-cache'
  });
  const [sendInviteEmail] = useMutation(SEND_USER_INVITE_EMAIL);
  const [sendLicenseAssignmentEmail] = useMutation(SEND_LICENSE_ASSIGNMENT_EMAIL);
  const [createUser, { loading: createUserLoading }] = useMutation(
    CREATE_USER,
    getAdminUserManagementUsers()
  );
  const [
    updateUserLicenses,
    { data: updateUserLicensesResponse, loading: updateUserLicensesLoading }
  ] = useMutation(UPDATE_USER_LICENSES, getAdminUserManagementUsers());

  const licenseUsage = useMemo(
    () =>
      (myOrgsResponse?.me?.organizationAssociations[0]?.organization.licenseUsage ||
        []) as OrganizationLicenseUsage[],
    [myOrgsResponse]
  );

  const handleFormSubmit = useCallback(
    async values => {
      const { data: createUserResponse } = await createUser({
        variables: {
          data: buildCreateUserPayload(values),
          upsert: true
        }
      });
      const user = createUserResponse.createUser;
      if (createUserResponse && createUserResponse.createUser) {
        const licenseUpdateData = buildUpdateUserLicensePayload(values, user);
        await updateUserLicenses({
          variables: {
            data: licenseUpdateData
          }
        });
        if (me) {
          const emailContacts = buildEmailUsers(me, user);
          await sendInviteEmail({
            variables: {
              data: {
                ...emailContacts,
                organizationInvites: values.organizationRoles.map(
                  ({
                    organization,
                    role
                  }: {
                    organization: Organization;
                    role: OrganizationRole;
                  }) => ({
                    organizationName: organization.name,
                    organizationRole: role.displayName
                  })
                )
              }
            }
          });
          if (values.licensedProducts?.length) {
            await sendLicenseAssignmentEmail({
              variables: {
                data: {
                  ...emailContacts,
                  productNames: values.licensedProducts.map(
                    ({ product }: ProductWithCurriculum) => product.name
                  )
                }
              }
            });
          }
        }
      }

      openAlert({
        variant: AlertVariant.SUCCESS,
        title: t('Invite Sent'),
        text: t('An invite has been sent to the user.')
      });
    },
    [createUser, openAlert, t, updateUserLicenses, sendInviteEmail, sendLicenseAssignmentEmail, me]
  );

  useEffect(() => {
    if (updateUserLicensesResponse && updateUserLicensesResponse.bulkLicenseAction) {
      closeModal();
    }
  }, [updateUserLicensesResponse, closeModal]);

  return (
    <Formik
      initialValues={{
        firstName: '',
        lastName: '',
        email: '',
        organizationRoles: [],
        licensedProducts: [],
        organization: '',
        role: '',
        selectedProduct: ''
      }}
      onSubmit={handleFormSubmit}
      validationSchema={InviteUserModalSchema}
      validateOnChange={false}
    >
      {(formikProps): ReactElement => (
        <Query
          query={GET_ORGANIZATIONS_AND_ROLES_QUERY}
          variables={{
            rolesWhere: {
              organizationId: Number(formikProps.values.organization)
            }
          }}
        >
          {({ data }: QueryResult<any, Record<string, any>>): ReactElement => {
            return (
              <ModalContainer
                Header={<ModalHeader title={t('Invite New User')} />}
                Footer={
                  <ModalFooter
                    Actions={
                      <>
                        <Button
                          variant="outlined"
                          color="secondary"
                          disableElevation
                          onClick={closeModal}
                        >
                          {t('Cancel', { ns: '@common' })}
                        </Button>
                        <Button
                          disabled={
                            !formikProps.isValid ||
                            formikProps.isSubmitting ||
                            createUserLoading ||
                            updateUserLicensesLoading
                          }
                          variant="contained"
                          color="primary"
                          disableElevation
                          onClick={formikProps.submitForm}
                        >
                          {t('Invite User')}
                        </Button>
                      </>
                    }
                  />
                }
              >
                <ModalContent>
                  <Form
                    {...formikProps}
                    licenseUsage={licenseUsage}
                    organizations={data?.organizations}
                    organizationRoles={data?.organizationRoles}
                  />
                </ModalContent>
              </ModalContainer>
            );
          }}
        </Query>
      )}
    </Formik>
  );
};

export default InviteUserModal;
